import React, { useEffect, useState } from "react"
import { Link } from "react-router"
import { useTranslation } from "react-i18next"

import { GET, httpOk } from "helpers/api"
import { formatDate, formatMoney } from "helpers/helpers"
import HintIcon from "pages/parts/HintIcon"
import { rateBasisName } from "pages/main/employeeTimeRates/Index"

function rateUnitLabel(t, basis) {
	if (basis === "attendance_days" || basis === "sales_days") return t("days")
	if (basis === "schedule_days") return t("shift")
	return t("hourly")
}

function periodLabel(t, item) {
	const start = item.start_date ? formatDate(item.start_date) : ""
	const end = item.end_date ? formatDate(item.end_date) : ""
	if (start && end) return `${start} — ${end}`
	return start ? `${t("from")} ${start}` : ""
}

/**
 * Вкладка «Начисления» в карточке сотрудника: что назначено — оклад/ставка должности,
 * оплата по времени, мотивационные программы, налоги. Только просмотр: правится всё
 * в своих разделах, из строки есть ссылка туда.
 */
function EmployeeAccruals({ employeeId, taxRates, positionTaxRates }) {
	const { t } = useTranslation()

	const [data, setData] = useState(null)

	async function getData() {
		const response = await GET(`/employees/${employeeId}/accruals`, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	useEffect(() => {
		getData()
	}, [employeeId]) // eslint-disable-line react-hooks/exhaustive-deps

	function sourceBadge(source) {
		return source === "user"
			? <span className="badge bg-info">{t("accrual_source_user")}</span>
			: <span className="badge bg-secondary">{t("accrual_source_position")}</span>
	}

	function stateBadge(item) {
		if (item.state === "active") return <span className="badge bg-success">{t("accrual_state_active")}</span>
		return <span className="badge bg-light text-dark border">{t(`accrual_state_${item.state}`)}</span>
	}

	function effectiveText(source) {
		if (source === "time_rate") return t("accrual_effective_time_rate")
		if (source === "position") return t("accrual_effective_position")
		if (source === "kpi") return t("accrual_effective_kpi")
		if (source === "custom") return t("accrual_effective_custom")
		return t("accrual_effective_none")
	}

	function positionAmount(position) {
		if (!position) return ""
		if ((position.salary_type_id === 1 || position.salary_type_id === 2) && position.time_rate) return formatMoney(position.time_rate)
		if (position.salary_type_id === 3 && position.salary) return formatMoney(position.salary)
		return ""
	}

	// Действующие и будущие — списком, выключенные и прошедшие — свёрнуто
	function splitByState(list) {
		return {
			current: list.filter(item => item.state === "active" || item.state === "upcoming"),
			archive: list.filter(item => item.state !== "active" && item.state !== "upcoming"),
		}
	}

	function renderTimeRate(item) {
		return (
			<div className="py-2" key={item.id} style={{ borderBottom: "1px solid var(--dark-border, #eee)" }}>
				<div className="d-flex justify-content-between gap-2">
					<Link to={`/employee-time-rates/update/${item.id}`} title={t("accrual_open")}>{item.name}</Link>
					<div className="d-flex gap-1 flex-shrink-0 align-items-start">
						{sourceBadge(item.source)}
						{item.is_effective ? <span className="badge bg-success">{t("accrual_applied")}</span> : stateBadge(item)}
					</div>
				</div>
				<div className="text-muted fz-12">
					{rateBasisName(t, item.rate_basis)} · {formatMoney(item.rate)} / {rateUnitLabel(t, item.rate_basis)}
					{periodLabel(t, item) ? ` · ${periodLabel(t, item)}` : ""}
				</div>
			</div>
		)
	}

	function renderMotivation(item) {
		return (
			<div className="py-2" key={item.id} style={{ borderBottom: "1px solid var(--dark-border, #eee)" }}>
				<div className="d-flex justify-content-between gap-2">
					<Link to={`/motivations/update/${item.id}`} title={t("accrual_open")}>{item.name}</Link>
					<div className="d-flex gap-1 flex-shrink-0 align-items-start">
						{sourceBadge(item.source)}
						{stateBadge(item)}
					</div>
				</div>
				<div className="text-muted fz-12">
					{item.calculation_type_name}
					{periodLabel(t, item) ? ` · ${periodLabel(t, item)}` : ""}
				</div>
			</div>
		)
	}

	function renderArchive(list, renderItem) {
		if (list.length === 0) return null
		return (
			<details className="mt-1">
				<summary className="text-muted fz-12 cursor">{t("accrual_archive")} ({list.length})</summary>
				{list.map(renderItem)}
			</details>
		)
	}

	if (!data) return null

	const timeRates = splitByState(data.time_rates || [])
	const motivations = splitByState(data.motivations || [])
	const taxes = { ...(positionTaxRates || {}) }
	;["income_tax_rate", "pension_rate", "social_tax_rate"].forEach((field) => {
		if (taxRates && taxRates[field] !== null && taxRates[field] !== undefined && taxRates[field] !== "") taxes[field] = taxRates[field]
	})

	return (
		<div>
			{/* Схема оплаты */}
			<h6 className="mb-1">{t("accrual_scheme")}<HintIcon text={t("accrual_scheme_hint")} /></h6>
			{data.position ?
				<div className="mb-3">
					<div>
						<Link to={`/employee-positions/update/${data.position.id}`} title={t("accrual_open")}>{data.position.name}</Link>
						{data.position.salary_type_name ? ` · ${data.position.salary_type_name}` : ""}
						{positionAmount(data.position) ? ` · ${positionAmount(data.position)}` : ""}
					</div>
					<div className="text-muted fz-12">{effectiveText(data.effective_source)}</div>
				</div>
				:
				<div className="text-muted mb-3">{t("accrual_no_position")}</div>
			}

			{/* Оплата по времени */}
			<h6 className="mb-1">{t("time_rates")}<HintIcon text={t("accrual_time_rate_hint")} /></h6>
			<div className="mb-3">
				{timeRates.current.length === 0 && <div className="text-muted">{t("accrual_empty")}</div>}
				{timeRates.current.map(renderTimeRate)}
				{renderArchive(timeRates.archive, renderTimeRate)}
			</div>

			{/* Мотивационные программы */}
			<h6 className="mb-1">{t("motivation_programs")}<HintIcon text={t("accrual_motivation_hint")} /></h6>
			<div className="mb-3">
				{motivations.current.length === 0 && <div className="text-muted">{t("accrual_empty")}</div>}
				{motivations.current.map(renderMotivation)}
				{renderArchive(motivations.archive, renderMotivation)}
			</div>

			{/* Старый КПИ — только пока у заведения нет активных мотиваций */}
			{data.kpi?.length > 0 &&
				<>
					<h6 className="mb-1">{t("accrual_kpi_legacy")}<HintIcon text={t("accrual_kpi_hint")} /></h6>
					<div className="mb-3">
						{data.kpi.map((item) => (
							<div className="py-2" key={item.id} style={{ borderBottom: "1px solid var(--dark-border, #eee)" }}>
								<Link to="/employee-bonus" title={t("accrual_open")}>{item.bonus_type_name}</Link>
								<span className="text-muted fz-12">
									{Number(item.bonus) > 0 ? ` · ${Number(item.bonus)}%` : ""}
									{Number(item.bonus_fixed) > 0 ? ` · ${formatMoney(item.bonus_fixed)}` : ""}
								</span>
							</div>
						))}
					</div>
				</>
			}

			{/* Налоги: итоговые ставки (личные поверх должности) */}
			{positionTaxRates &&
				<>
					<h6 className="mb-1">{t("salary_taxes")}<HintIcon text={t("salary_taxes_hint")} /></h6>
					<div className="text-muted">
						{t("income_tax_rate")}: {Number(taxes.income_tax_rate ?? 0)} · {t("pension_rate")}: {Number(taxes.pension_rate ?? 0)} · {t("social_tax_rate")}: {Number(taxes.social_tax_rate ?? 0)}
					</div>
				</>
			}
		</div>
	)
}

export default EmployeeAccruals
